/**
 * CollectionPage — the Prompt-Brush 1.0 archive: every prompt drawn by hand, browsable,
 * with a "request a 13 × 16 giclée print" action on each.
 *
 * Drawings are extracted from the book PDF (black ink on white). The card background is
 * the brand's warm paper and the image uses mix-blend-multiply, so the white drops out
 * and only the ink sits on the paper — matching the gallery's look.
 *
 * The full 1,541 are bundled as ~560px WebP under public/collection/. They render in
 * chunks (IntersectionObserver) so the grid stays smooth.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown } from 'lucide-react';
import collection from '@/data/collection.json';
import { VersionSwitcher } from '@/components/VersionSwitcher';

const MONO = 'FK Grotesk Mono, monospace';
const DISPLAY = 'Delcan Mono, monospace';
const SERIF = 'Martina Plantijn, Georgia, serif';
const DELCAN_SIZE = 'text-2xl';
const CHUNK = 120; // cards rendered per "page" as you scroll

// Per-size Stripe Payment Links. 13×16 is signed; the larger sizes are unsigned. Each
// checkout appends ?client_reference_id=pb-<page> so the order is tagged with the exact
// drawing in Stripe. A size with an empty `link` shows "coming soon" until one is added.
type PrintSize = { id: string; size: string; signed: boolean; price: string; ratio: string; link: string };
const PRINT_SIZES: PrintSize[] = [
  // The signed 13 × 17 is sold at delcan.co (one shop for everything); the link opens that drawing there.
  { id: 'sm', size: '13 × 17 in', signed: true,  price: '$45',  ratio: '13 / 17', link: 'https://delcan.co/prints' },
  { id: 'md', size: '18 × 24 in', signed: false, price: '$130', ratio: '18 / 24', link: 'https://buy.stripe.com/dRmfZi9Ov7Xd6Aacof7kc02' },
  { id: 'lg', size: '24 × 32 in', signed: false, price: '$175', ratio: '24 / 32', link: 'https://buy.stripe.com/cNi3cw1hZcdt7EefAr7kc03' },
];

// View sizes — fewer columns = bigger drawings. Index 1 (Medium) is the default.
const SIZES = [
  { key: 'S', cols: 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-6', gap: 'gap-5 md:gap-6' },
  { key: 'M', cols: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4', gap: 'gap-8 md:gap-10' },
  { key: 'L', cols: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3', gap: 'gap-10 md:gap-14' },
] as const;

type Item = { page: number; prompt: string; file: string };

const num = (p: number) => '#' + String(p).padStart(4, '0');

function Card({ it, big, onPreview }: { it: Item; big: boolean; onPreview: (it: Item) => void }) {
  return (
    <div className="flex flex-col">
      <div
        className="border border-border overflow-hidden"
        style={{ background: '#F4F0E6', aspectRatio: '5/7' }}
      >
        <img
          src={`/collection/${it.file}`}
          alt={it.prompt || `Drawing ${num(it.page)}`}
          loading="lazy"
          decoding="async"
          className="w-full h-full"
          style={{ objectFit: 'contain', mixBlendMode: 'multiply' }}
        />
      </div>
      {!!it.prompt && (
        <div
          className="text-foreground mt-3"
          style={{ fontFamily: DISPLAY, fontSize: big ? '16px' : '13px', lineHeight: 1.3 }}
        >
          {it.prompt}
        </div>
      )}
      <div className="flex items-center justify-between mt-2 gap-2">
        <span className="text-muted-foreground uppercase tracking-widest" style={{ fontSize: '9pt', fontFamily: MONO }}>
          {num(it.page)}
        </span>
        <button
          onClick={() => onPreview(it)}
          className="text-foreground hover:underline whitespace-nowrap"
          style={{ fontFamily: MONO, fontSize: '9.5pt' }}
        >
          Request a print →
        </button>
      </div>
    </div>
  );
}

/** Framed preview of the print, shown before sending the buyer to Stripe checkout. */
function PrintPreview({ it, onClose }: { it: Item; onClose: () => void }) {
  const [sizeId, setSizeId] = useState(PRINT_SIZES[0].id);
  const sz = PRINT_SIZES.find((s) => s.id === sizeId) || PRINT_SIZES[0];
  const checkoutHref = !sz.link ? '' : sz.link.startsWith('https://delcan.co')
    ? `${sz.link}/${it.page}?utm_source=prompt-brush.com&utm_medium=referral`
    : `${sz.link}?client_reference_id=pb-${it.page}`;
  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ background: 'rgba(20,18,16,0.6)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Print preview"
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-background overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* The drawing on white paper with a mat — a framed-print look */}
        <div className="px-8 pt-10 pb-2">
          <div
            className="mx-auto bg-white border border-border"
            style={{ maxWidth: 300, aspectRatio: sz.ratio, boxShadow: '0 10px 30px rgba(0,0,0,0.18)', padding: '8%' }}
          >
            <img
              src={`/collection/${it.file}`}
              alt={it.prompt || `Drawing ${num(it.page)}`}
              className="w-full h-full"
              style={{ objectFit: 'contain' }}
            />
          </div>
        </div>

        <div className="px-8 pb-8 pt-4 text-center">
          {!!it.prompt && (
            <div className="text-foreground" style={{ fontFamily: DISPLAY, fontSize: '18px', lineHeight: 1.3 }}>
              {it.prompt}
            </div>
          )}
          {/* Size selector */}
          <div className="relative inline-block mt-4">
            <select
              value={sizeId}
              onChange={(e) => setSizeId(e.target.value)}
              aria-label="Print size"
              className="appearance-none rounded-full border border-border bg-card text-foreground pl-4 pr-9 py-2.5 text-sm cursor-pointer focus:border-primary focus:outline-none"
              style={{ fontFamily: MONO }}
            >
              {PRINT_SIZES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.size}{s.signed ? ' · signed' : ''} — {s.price}{s.link ? '' : ' (soon)'}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" aria-hidden="true" />
          </div>

          <div className="text-muted-foreground uppercase tracking-widest mt-3" style={{ fontFamily: MONO, fontSize: '9pt' }}>
            {num(it.page)} · {sz.signed ? 'Signed archival giclée' : 'Archival giclée'}
          </div>

          {checkoutHref ? (
            <a
              href={checkoutHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="block w-full rounded-full bg-foreground text-background py-3.5 mt-5 hover:opacity-80 transition-opacity"
              style={{ fontFamily: MONO, fontSize: '11pt' }}
            >
              Continue to checkout · {sz.price} →
            </a>
          ) : (
            <div
              className="block w-full rounded-full border border-border text-muted-foreground py-3.5 mt-5 uppercase tracking-widest cursor-default"
              style={{ fontFamily: MONO, fontSize: '9pt' }}
            >
              {sz.size} coming soon
            </div>
          )}
          <button
            onClick={onClose}
            className="mt-3 text-muted-foreground hover:text-foreground transition-colors"
            style={{ fontFamily: MONO, fontSize: '10pt' }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

export function CollectionPage() {
  const all = collection as Item[];
  const [query, setQuery] = useState('');
  const [size, setSize] = useState(1); // default Medium
  const [visible, setVisible] = useState(CHUNK);
  const [preview, setPreview] = useState<Item | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (it) => it.prompt.toLowerCase().includes(q) || num(it.page).toLowerCase().includes(q)
    );
  }, [all, query]);

  // Reset the render window whenever the search changes.
  useEffect(() => {
    setVisible(CHUNK);
    scrollRef.current?.scrollTo({ top: 0 });
  }, [query]);

  // Load the next chunk as the sentinel nears the viewport.
  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible((v) => v + CHUNK);
      },
      { root, rootMargin: '800px' }
    );
    io.observe(target);
    return () => io.disconnect();
  }, [items.length, visible]);

  // Close the print preview on Escape.
  useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreview(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  const sz = SIZES[size];
  const shown = items.slice(0, visible);

  return (
    <div ref={scrollRef} className="h-screen overflow-y-auto">
      <div className="container mx-auto px-4 py-16 md:py-24 max-w-6xl">
        <div className="max-w-2xl mb-12">
          <div className="text-muted-foreground uppercase tracking-widest mb-4" style={{ fontSize: '9pt', fontFamily: MONO }}>
            Prompt-Brush 1.0
          </div>
          <h1 className={`text-foreground ${DELCAN_SIZE} leading-tight mb-3`} style={{ fontFamily: DISPLAY }}>
            The original drawings
          </h1>
          <p className="text-foreground leading-relaxed" style={{ fontFamily: SERIF, fontSize: '15pt' }}>
            Every prompt people sent in for Prompt-Brush 1.0, drawn by hand — no AI. Browse the whole
            archive, and order an archival giclée print of any one you love.
          </p>
        </div>

        {/* Controls — sticky so search + size stay reachable while scrolling */}
        <div className="sticky top-0 z-10 -mx-4 px-4 py-3 mb-10 bg-background/95 backdrop-blur-sm border-b border-border flex items-center gap-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search prompts…"
              className="w-full bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
              style={{ fontFamily: MONO, fontSize: '11pt' }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="text-muted-foreground hover:text-foreground shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <VersionSwitcher current="1.0" />

          <div className="flex items-center gap-1 shrink-0">
            {SIZES.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setSize(i)}
                aria-label={`Size ${s.key}`}
                className={`w-7 h-7 flex items-center justify-center transition-colors ${
                  i === size ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                style={{ fontFamily: MONO, fontSize: '10pt' }}
              >
                {s.key}
              </button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <p className="text-muted-foreground py-16" style={{ fontFamily: MONO, fontSize: '11pt' }}>
            No prompts match “{query}”.
          </p>
        ) : (
          <>
            <div className={`grid ${sz.cols} ${sz.gap}`}>
              {shown.map((it) => (
                <Card key={it.page} it={it} big={size === 2} onPreview={setPreview} />
              ))}
            </div>
            {visible < items.length && <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />}
          </>
        )}

        <p className="text-muted-foreground mt-16" style={{ fontFamily: MONO, fontSize: '10pt' }}>
          {query
            ? `${items.length.toLocaleString()} ${items.length === 1 ? 'match' : 'matches'} for “${query}”`
            : `${all.length.toLocaleString()} drawings — the complete Prompt-Brush 1.0 archive.`}
        </p>
      </div>

      {preview && createPortal(<PrintPreview it={preview} onClose={() => setPreview(null)} />, document.body)}
    </div>
  );
}
